/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import {
    FPSectionCard, FPSectionHeader, FPSectionBody,
    FPTimelineContainer, FPTimelineItem, FPTimelineCard,
    FPTimelineDate, FPTimelineMethod, FPTimelineMeta, FPTimelineTag,
    FPTimelineActions, FPTimelineBtn,
    FPRenewalCard, FPRenewalInfo, FPRenewalMethod, FPRenewalDate,
    FPAlertBanner, FPAlertIcon, FPAlertContent, FPAlertTitle, FPAlertDetail, FPAlertAction,
    FPEmptyState, FPEmptyIcon, FPEmptyTitle, FPEmptyDesc,
    FPStatusBadge, FPPrimaryBtn, FPDangerBtn,
    FPFormGroup, FPSelect, FPLabel,
} from "./FamilyPlanningStyles";

/* ═══════════════════════════════════════════════════════════════
   FPHistoryTimeline — Family Planning History Timeline (Task 17)
   Shows FP records chronologically, renewal alerts, and
   method discontinuation workflow.
   ═══════════════════════════════════════════════════════════════ */

const alertIcons = { critical: "🚨", warning: "⚠️", info: "ℹ️", success: "✅" };

const STATUS_COLORS = {
    active: "#2e7d32",
    discontinued: "#c62828",
    switched: "#f57f17",
    expired: "#e65100",
};

export const FPHistoryTimeline = function (props) {
    const fp = props.fp;

    /* ─── Discontinue dialog state ─── */
    const discState = useState(null);
    const discontinueTarget = discState[0];
    const setDiscontinueTarget = discState[1];

    const reasonState = useState("");
    const discReason = reasonState[0];
    const setDiscReason = reasonState[1];

    /* ─── Sort records by date descending ─── */
    const sortedRecords = fp.fpRecords.slice().sort(function (a, b) {
        return new Date(b.provision_date).getTime() - new Date(a.provision_date).getTime();
    });

    const handleDiscontinue = function () {
        if (!discReason) return;
        fp.discontinueMethod(discontinueTarget.id, discReason);
        setDiscontinueTarget(null);
        setDiscReason("");
    };

    return (
        <div>

            {/* ─── Discontinue Dialog ─── */}
            {discontinueTarget && (
                <FPSectionCard>
                    <FPSectionHeader color="#c62828">
                        ⚠️ Discontinue Method — {fp.getMethodLabel(discontinueTarget.method_provided)}
                    </FPSectionHeader>
                    <FPSectionBody>
                        <FPFormGroup>
                            <FPLabel>Reason for Discontinuation *</FPLabel>
                            <FPSelect value={discReason} onChange={function (e) { setDiscReason(e.target.value); }}>
                                <option value="">— Select reason —</option>
                                {fp.DISCONTINUATION_REASONS.map(function (r) {
                                    return <option key={r.value} value={r.value}>{r.label}</option>;
                                })}
                            </FPSelect>
                        </FPFormGroup>
                        <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                            <FPDangerBtn onClick={handleDiscontinue} disabled={!discReason}>
                                Confirm Discontinuation
                            </FPDangerBtn>
                            <FPPrimaryBtn onClick={function () { setDiscontinueTarget(null); setDiscReason(""); }}
                                style={{ background: "#757575" }}>
                                Cancel
                            </FPPrimaryBtn>
                        </div>
                    </FPSectionBody>
                </FPSectionCard>
            )}

            {/* ─── Timeline ─── */}
            <FPSectionCard>
                <FPSectionHeader color="#005a8b">
                    FP History ({sortedRecords.length} record{sortedRecords.length !== 1 ? "s" : ""})
                </FPSectionHeader>
                <FPSectionBody>
                    {sortedRecords.length === 0 ? (
                        <FPEmptyState>
                            <FPEmptyIcon>💊</FPEmptyIcon>
                            <FPEmptyTitle>No FP Records Yet</FPEmptyTitle>
                            <FPEmptyDesc>
                                No family planning records found. Switch to the "FP Service" tab to record a new service.
                            </FPEmptyDesc>
                        </FPEmptyState>
                    ) : (
                        <FPTimelineContainer>
                            {sortedRecords.map(function (record) {
                                const statusColor = STATUS_COLORS[record.status] || "#6a1b9a";
                                const clientTypeLabel = fp.CLIENT_TYPES.find(function (t) { return t.value === record.client_type; });
                                const sideEffectLabels = (record.side_effects || []).map(function (se) {
                                    const match = fp.SIDE_EFFECT_OPTIONS.find(function (o) { return o.value === se; });
                                    return match ? match.label : se;
                                });

                                return (
                                    <FPTimelineItem key={record.id} statusColor={statusColor}>
                                        <FPTimelineCard>
                                            <FPTimelineDate>
                                                {record.provision_date}
                                                {record.provided_by && " — " + record.provided_by}
                                            </FPTimelineDate>
                                            <FPTimelineMethod>
                                                {fp.getMethodLabel(record.method_provided)}
                                            </FPTimelineMethod>
                                            <FPTimelineMeta>
                                                <FPStatusBadge color={statusColor}>
                                                    {record.status === "active" ? "● Active" :
                                                        record.status === "discontinued" ? "✕ Discontinued" :
                                                            record.status === "switched" ? "↻ Switched" :
                                                                record.status === "expired" ? "⏰ Expired" : record.status}
                                                </FPStatusBadge>
                                                {clientTypeLabel && (
                                                    <FPTimelineTag color="#6a1b9a">
                                                        {clientTypeLabel.label}
                                                    </FPTimelineTag>
                                                )}
                                                {record.expected_renewal_date && (
                                                    <FPTimelineTag color="#e65100">
                                                        Renewal: {record.expected_renewal_date}
                                                    </FPTimelineTag>
                                                )}
                                            </FPTimelineMeta>

                                            {/* Side effects */}
                                            {sideEffectLabels.length > 0 && sideEffectLabels[0] !== "No Side Effects" && (
                                                <div style={{ marginTop: "6px" }}>
                                                    <span style={{ fontSize: "0.78rem", color: "#c62828", fontWeight: 600 }}>
                                                        Side effects:{" "}
                                                    </span>
                                                    <span style={{ fontSize: "0.78rem", color: "#555" }}>
                                                        {sideEffectLabels.join(", ")}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Switched from */}
                                            {record.switched_from && (
                                                <div style={{ marginTop: "4px", fontSize: "0.78rem", color: "#666" }}>
                                                    Switched from: {fp.getMethodLabel(record.switched_from)}
                                                    {record.switch_reason && " — " + record.switch_reason}
                                                </div>
                                            )}

                                            {/* Discontinuation reason */}
                                            {record.discontinuation_reason && (
                                                <div style={{ marginTop: "4px", fontSize: "0.78rem", color: "#c62828" }}>
                                                    Reason: {
                                                        (fp.DISCONTINUATION_REASONS.find(function (r) { return r.value === record.discontinuation_reason; }) || {}).label
                                                        || record.discontinuation_reason
                                                    }
                                                </div>
                                            )}

                                            {/* Counselling notes */}
                                            {record.counselling_notes && (
                                                <div style={{ marginTop: "6px", fontSize: "0.8rem", color: "#555", fontStyle: "italic" }}>
                                                    "{record.counselling_notes}"
                                                </div>
                                            )}

                                            {/* Vitals */}
                                            {(record.bp_systolic || record.weight_check) && (
                                                <div style={{ marginTop: "6px", fontSize: "0.78rem", color: "#666", display: "flex", gap: "16px" }}>
                                                    {record.bp_systolic && (
                                                        <span>BP: {record.bp_systolic}/{record.bp_diastolic} mmHg</span>
                                                    )}
                                                    {record.weight_check && (
                                                        <span>Weight: {record.weight_check} kg</span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Actions */}
                                            <FPTimelineActions>
                                                <FPTimelineBtn color="#6a1b9a" onClick={function () { fp.startEditRecord(record); }}>
                                                    Edit
                                                </FPTimelineBtn>
                                                {record.status === "active" && (
                                                    <FPTimelineBtn color="#c62828" onClick={function () {
                                                        setDiscontinueTarget(record);
                                                        setDiscReason("");
                                                    }}>
                                                        Discontinue
                                                    </FPTimelineBtn>
                                                )}
                                            </FPTimelineActions>
                                        </FPTimelineCard>
                                    </FPTimelineItem>
                                );
                            })}
                        </FPTimelineContainer>
                    )}
                </FPSectionBody>
            </FPSectionCard>
        </div>
    );
};
